import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from "../../services/api";
import { LOGIN_URL } from "../../services/urls";
import { getToken, setToken, setPharmacyId } from '../../utils/commonFunctions';

// Initial state
const initialState = {
  loading: false,
  error: null,
  user: null,
  token: getToken() || null, // If token exists in localStorage, use it
};

/** First pharmacy id from login payload (comma-separated or array). */
const resolveDefaultPharmacyId = (pharmacyId) => {
  if (!pharmacyId) return null;
  if (Array.isArray(pharmacyId)) {
    return pharmacyId[0] ? String(pharmacyId[0]) : null;
  }
  const first = String(pharmacyId).split(',').map((id) => id.trim()).find(Boolean);
  return first || null;
};

// Async thunk for login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post(LOGIN_URL, credentials);
      const { accessToken, ...userDetails } = response.data.data;
      setToken(accessToken);
      const defaultPharmacyId = resolveDefaultPharmacyId(userDetails.pharmacyId);
      if (defaultPharmacyId) {
        setPharmacyId(defaultPharmacyId);
      }
      return { token: accessToken, user: userDetails };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Login failed. Please try again.");
    }
  }
);

// Create slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      localStorage.clear();
      state.user = null;
      state.token = null;
    },
    clearError: (state) => {
      state.error = null; // Clear the error state manually
    },
    clearLoading: (state) => {
      state.loading = null; // Clear the error state manually
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Export actions and reducer
export const { logout, clearError, clearLoading } = authSlice.actions;
export default authSlice.reducer;
